import { getEnvironmentApiUrl } from '../services/General';
import { getUser } from '../services/Auth';

import FiltersContext from '../context/FiltersContext';
import ColumnsContext from '../context/ColumnsContext';

import { useState, useContext, useCallback } from 'react';
import useHttp from './useHttp';

import { UserUIPersistence, UIPersistenceKeys, PatientFilterKeys, PollFilterKeys, ColumnsFilterKeys, AppName } from '../interfaces/UserInfo';

const useUIPersistance = () => {

    const { dispatchPatientsFilters, dispatchPollsFilters } = useContext(FiltersContext);
    const { dispatchColumns, dispatchSonarSelectedCells } = useContext(ColumnsContext);

    const { sendRequest: getUserUIPersistence } = useHttp<UserUIPersistence[]>();
    const { sendRequest: setUserUIPersistence } = useHttp();

    const getNumberArray = (uipersistence: UserUIPersistence | undefined) => uipersistence === undefined || uipersistence.value.trim() === '' ?
        [] : uipersistence.value.split(',').map(id => Number(id)) || [];

    type AllowedExtensions<T> =
        T extends ColumnsFilterKeys ? 'columns' :
        T extends PatientFilterKeys ? 'filter_patient' :
        T extends PollFilterKeys ? 'filter_polls' :
        never;

    const getFilters = useCallback(<T extends ColumnsFilterKeys | PatientFilterKeys | PollFilterKeys>(
        uiData: UserUIPersistence[],
        keys: T[], initObject: Record<T, number[]>,
        extension: AllowedExtensions<T>
    ) => {
        const defaultObject = { ...initObject };
        keys.forEach(key => {
            const persistenceKey = `bchome_${extension}_${key}`;
            const uipersistence = uiData.find(ui => ui.key === persistenceKey);
            defaultObject[key] = getNumberArray(uipersistence);
        });
        return defaultObject;
    }, []);

    const parseUIPersistence = useCallback((uiData: UserUIPersistence[]) => {

        const appName: AppName = 'bchome';
        const bchomeUIData = uiData.filter(ui => ui.key.startsWith(appName));

        const initPatientFilters: Record<PatientFilterKeys, number[]> = { profileIDs: [], daysEntryRange: [] };
        const patientFiltersDefault = getFilters<PatientFilterKeys>(bchomeUIData, ['profileIDs', 'daysEntryRange'], initPatientFilters, 'filter_patient');
        dispatchPatientsFilters({ type: 'SET_DEFAULT_FILTERS', ...patientFiltersDefault });

        const initPolls: Record<PollFilterKeys, number[]> = { profileIDs: [], frequencyIDs: [], showTablesIDs: [], typeIDs: [], scoreAlertsIDs: [] };
        const pollFiltersDefault = getFilters<PollFilterKeys>(bchomeUIData, ['frequencyIDs', 'showTablesIDs', 'profileIDs', 'typeIDs', 'scoreAlertsIDs'], initPolls, 'filter_polls');
        dispatchPollsFilters({ type: 'SET_DEFAULT', ...pollFiltersDefault });

        const initColumns: Record<ColumnsFilterKeys, number[]> = { patientIDs: [], sonarIDs: [] };
        const columnsFiltersDefault = getFilters<ColumnsFilterKeys>(bchomeUIData, ['patientIDs', 'sonarIDs'], initColumns, 'columns');

        const patientIDs = columnsFiltersDefault.patientIDs.length > 0
            ? Array.from(new Set([...columnsFiltersDefault.patientIDs, 25]))
            : columnsFiltersDefault.patientIDs;

        dispatchColumns({
            type: 'SET_DEFAULT_VISIBILITY',
            ...columnsFiltersDefault,
            patientIDs
        });

        const selectedSonarIDs = uiData.find(d => d.key === 'bchome_selected_sonar_IDs');
        if (selectedSonarIDs) dispatchSonarSelectedCells({ type: 'SET_DEFAULT', cellTuples: JSON.parse(selectedSonarIDs.value) });
    }, [dispatchColumns, dispatchPatientsFilters, dispatchPollsFilters, dispatchSonarSelectedCells, getFilters]);

    const getUIPersistence = useCallback(() => {
        getUserUIPersistence({
            url: getEnvironmentApiUrl() + 'user_ui_persistence/all',
            method: 'POST',
            body: { params: {}, filter: { user_id: getUser().id }, },
        }, parseUIPersistence);
    }, [getUserUIPersistence, parseUIPersistence]);

    const set = useCallback((key: UIPersistenceKeys, value: string) => {
        setUserUIPersistence({
            url: getEnvironmentApiUrl() + 'user_ui_persistence/set',
            method: 'POST',
            body: { params: {}, filter: { user_id: getUser().id, case_id: null, key, value } }
        });
    }, [setUserUIPersistence]);

    return { getUIPersistence, set };

};

export default useUIPersistance;